import React, {createContext, useContext, useState} from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import useTkrStoreItemCount from '../hooks/useTkrStoreItemCount';
import SummaryContext from './SummaryContext';
import ItemsContext from './ItemsContext';
import CoupleContext from './CoupleContext';
import UserStateContext from './UserStateContext';
import RegistryCoreApi from '../services/RegistryCoreApi';
import useSegment from '../hooks/useSegment';
import isSyncRegistryProductsSuccessful from '../utils/isSyncRegistryProductsSuccessful';

const Context = createContext({
  refresh: noop,
  refreshNonItemData: noop,
  onSyncRegistryProducts: noop,
  tkrStoreItemCount: 0,
  hasShippingAddress: true,
});

export default Context;

export const DataSyncContextProvider = ({children}) => {
  const {fetchSummary} = useContext(SummaryContext);
  const {fetchItems} = useContext(ItemsContext);
  const {updateSyncRegistryProductsStatus} = useContext(CoupleContext);
  const [
    isSyncRegistryProductsPending,
    setIsSyncRegistryProductsPending,
  ] = useState(false);
  const {tkrStoreItemCount, refreshTKRStoreItemCount} = useTkrStoreItemCount();
  const {userStateData} = useContext(UserStateContext);
  const {trackSegmentData} = useSegment();

  const refresh = () => {
    refreshTKRStoreItemCount();
    fetchSummary();
    fetchItems();
  };

  const refreshNonItemData = () => {
    refreshTKRStoreItemCount();
    fetchSummary();
  };

  const trackSegmentEvent = (
    isSyncRegistryProductSuccessful,
    retailerName,
    result,
    eventData,
  ) => {
    const data = {
      selection: 'sync store products',
      source: 'manage registry',
      userDecisionArea: 'your linked stores',
      retailer: retailerName,
      result: isSyncRegistryProductSuccessful ? 'success' : 'failure',
      externalAPIResult: isSyncRegistryProductSuccessful
        ? ''
        : result?.retailerProductAPISyncResults || '',
      internalAPIResult: isSyncRegistryProductSuccessful
        ? ''
        : result?.retailerItemsAPISyncResults || '',
      ...eventData,
    };

    trackSegmentData({
      eventName: 'Registry Interaction',
      eventData: data,
    });
  };

  const handleSyncRegistryProducts = async (registries, eventData = {}) => {
    setIsSyncRegistryProductsPending(true);
    const partnerRetailerRegistries = registries.filter(
      registry => registry?.retailer?.isPartner,
    );
    partnerRetailerRegistries.forEach(registry => {
      updateSyncRegistryProductsStatus({
        lastRunTime: new Date().toISOString(),
        status: 'pending',
        registryId: registry.id,
      });
    });

    const result = await RegistryCoreApi.getRegistriesProductsSynchronizationResult(
      partnerRetailerRegistries,
    );

    Object.keys(result).forEach(resultKey => {
      const registry = partnerRetailerRegistries.find(
        reg => reg.registryUuid === resultKey,
      );

      const registryResult = result[resultKey];

      if (registryResult.error) {
        trackSegmentEvent(false, registry?.retailer?.name, {}, eventData);
        updateSyncRegistryProductsStatus({
          lastRunTime: new Date().toISOString(),
          status: 'failure',
          registryId: registry.id,
        });
      }

      const isSuccess = isSyncRegistryProductsSuccessful(registryResult.value);
      trackSegmentEvent(
        isSuccess,
        registry?.retailer?.name,
        registryResult.value,
        eventData,
      );
      updateSyncRegistryProductsStatus({
        lastRunTime: new Date().toISOString(),
        status: isSuccess ? 'Success' : 'failure',
        registryId: registry.id,
      });
    });

    setIsSyncRegistryProductsPending(false);
    const registryProductsSyncSuccess = Object.values(result).filter(
      promiseResult =>
        !promiseResult.error &&
        isSyncRegistryProductsSuccessful(promiseResult.value),
    );

    if (registryProductsSyncSuccess.length) {
      fetchItems();
    }
    return Object.values(result);
  };

  return (
    <Context.Provider
      value={{
        refresh,
        refreshNonItemData,
        isSyncRegistryProductsPending,
        onSyncRegistryProducts: handleSyncRegistryProducts,
        tkrStoreItemCount,
        hasShippingAddress: userStateData?.shippingAddress?.isSet,
      }}>
      {children}
    </Context.Provider>
  );
};

DataSyncContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
